import React, { useState, useEffect } from 'react';
import config from '../config';
import TransferOwnershipModal from './TransferOwnershipModal';

function WorkspacesContent({ user }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [expandedPatent, setExpandedPatent] = useState(null);
  const [patentComments, setPatentComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [newWorkspaceDesc, setNewWorkspaceDesc] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Load workspaces on mount
  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces?token=${token}`);
      const data = await response.json();
      if (response.ok) {
        setWorkspaces(data);
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
    setLoading(false);
  };

  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      alert('Please enter a workspace name');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newWorkspaceName, description: newWorkspaceDesc })
      });
      const data = await response.json();
      if (response.ok) {
        setShowCreateModal(false);
        setNewWorkspaceName('');
        setNewWorkspaceDesc('');
        loadWorkspaces();
      } else {
        alert(data.detail || 'Failed to create workspace');
      }
    } catch (error) {
      alert('Error creating workspace');
    }
  };

  const joinWorkspace = async () => {
    if (!inviteCode.trim()) {
      alert('Please enter an invite code');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/join?invite_code=${inviteCode}&token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        setShowJoinModal(false);
        setInviteCode('');
        loadWorkspaces();
        alert(data.message);
      } else {
        alert(data.detail || 'Failed to join workspace');
      }
    } catch (error) {
      alert('Error joining workspace');
    }
  };

  const loadWorkspaceDetails = async (workspaceId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${workspaceId}?token=${token}`);
      const data = await response.json();
      if (response.ok) {
        setSelectedWorkspace(data);
      }
    } catch (error) {
      console.error('Error loading workspace:', error);
    }
  };

  const deleteWorkspace = async (workspaceId) => {
    if (!window.confirm('Are you sure you want to delete this workspace? This cannot be undone.')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${workspaceId}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedWorkspace(null);
        loadWorkspaces();
      } else {
        alert(data.detail || 'Error deleting workspace');
      }
    } catch (error) {
      alert('Error deleting workspace');
    }
  };

  const transferOwnership = async (workspaceId, newOwnerEmail) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.API_URL}/api/workspaces/${workspaceId}/transfer-ownership?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_owner_email: newOwnerEmail })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Error transferring ownership');
    }
    setSelectedWorkspace(null);
    loadWorkspaces();
    alert('Ownership transferred successfully!');
  };

  const leaveWorkspace = async (workspaceId) => {
    if (!window.confirm('Are you sure you want to leave this workspace?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${workspaceId}/members/${user.email}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedWorkspace(null);
        loadWorkspaces();
      } else {
        alert(data.detail || 'Error leaving workspace');
      }
    } catch (error) {
      alert('Error leaving workspace');
    }
  };

  const copyInviteLink = (inviteCode) => {
    const link = `${window.location.origin}?join=${inviteCode}`;
    navigator.clipboard.writeText(link);
    alert('Invite link copied to clipboard!');
  };

  const loadComments = async (patentNumber) => {
    if (expandedPatent === patentNumber) {
      setExpandedPatent(null);
      setPatentComments([]);
      return;
    }
    setLoadingComments(true);
    setExpandedPatent(patentNumber);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${selectedWorkspace.id}/patents/${patentNumber}/comments?token=${token}`);
      const data = await response.json();
      if (response.ok) {
        setPatentComments(data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
    setLoadingComments(false);
  };

  const addComment = async (patentNumber) => {
    if (!newComment.trim()) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${selectedWorkspace.id}/patents/${patentNumber}/comments?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: newComment })
      });
      if (response.ok) {
        setNewComment('');
        loadComments(patentNumber);
        setExpandedPatent(null);
        setTimeout(() => loadComments(patentNumber), 100);
      }
    } catch (error) {
      alert('Error adding comment');
    }
  };

  const deleteComment = async (commentId, patentNumber) => {
    if (!window.confirm('Delete this comment?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${selectedWorkspace.id}/comments/${commentId}?token=${token}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        loadComments(patentNumber);
        setExpandedPatent(null);
        setTimeout(() => loadComments(patentNumber), 100);
      }
    } catch (error) {
      alert('Error deleting comment');
    }
  };

  const removePatentFromWorkspace = async (patentNumber) => {
    if (!window.confirm(`Remove patent ${patentNumber} from this workspace?`)) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${selectedWorkspace.id}/patents/${patentNumber}?token=${token}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        // Reload workspace to refresh patents list
        loadWorkspaceDetails(selectedWorkspace.id);
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to remove patent');
      }
    } catch (error) {
      alert('Error removing patent');
    }
  };

  // Check if user tier allows workspaces
  if (user.tier === 'free') {
    return (
      <main style={{ maxWidth: '600px', margin: '32px auto', padding: '0 16px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1d2934', marginBottom: '16px' }}>Collaborative Workspaces</h1>
        <div style={{ background: 'white', borderRadius: '8px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#5e778a', marginBottom: '24px' }}>
            Workspaces allow you to collaborate with your team by sharing patents, adding notes, and discussing findings together.
          </p>
          <p style={{ color: '#1d2934', fontWeight: '500', marginBottom: '24px' }}>
            Upgrade to Premium or Pro to create and join workspaces.
          </p>
          <button 
            onClick={() => window.location.href = '/?page=pricing'}
            style={{ background: '#53dafc', color: 'white', padding: '12px 24px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '16px' }}
          >
            View Pricing
          </button>
        </div>
      </main>
    );
  }

  // Workspace detail view
  if (selectedWorkspace) {
    return (
      <main style={{ maxWidth: '900px', margin: '32px auto', padding: '0 16px' }}>
        <button 
          onClick={() => setSelectedWorkspace(null)} 
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', marginBottom: '16px', fontSize: '15px' }}
        >
          ← Back to Workspaces
        </button>

        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1d2934', marginBottom: '8px' }}>{selectedWorkspace.name}</h1>
              {selectedWorkspace.description && (
                <p style={{ color: '#5e778a' }}>{selectedWorkspace.description}</p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => copyInviteLink(selectedWorkspace.invite_code)}
                style={{ background: '#f0f9ff', color: '#2563eb', padding: '8px 16px', borderRadius: '4px', border: '1px solid #2563eb', cursor: 'pointer', fontSize: '14px' }}
              >
                Copy Invite Link
              </button>
              {selectedWorkspace.my_role === 'owner' ? (
                <>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    style={{ background: '#f3e8ff', color: '#7c3aed', padding: '8px 16px', borderRadius: '4px', border: '1px solid #7c3aed', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Transfer Ownership
                  </button>
                  <button
                    onClick={() => deleteWorkspace(selectedWorkspace.id)}
                    style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 16px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Delete Workspace
                  </button>
                </>
              ) : (
                <button
                  onClick={() => leaveWorkspace(selectedWorkspace.id)}
                  style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 16px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                >
                  Leave Workspace
                </button>
              )}
            </div>
          </div>
          <div style={{ fontSize: '14px', color: '#5e778a' }}>
            Your role: <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{selectedWorkspace.my_role}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          {/* Shared Patents */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1d2934', marginBottom: '16px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
              Shared Patents ({selectedWorkspace.patents?.length || 0})
            </h2>
            {selectedWorkspace.patents?.length === 0 ? (
              <p style={{ color: '#5e778a', fontSize: '14px' }}>No patents shared yet. Share patents from search results using the "Share to Workspace" button.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedWorkspace.patents.map((patent) => (
                  <div key={patent.patent_number} style={{ padding: '12px', background: '#f5f7fa', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <a
                          href={`https://patents.google.com/patent/${/^[A-Za-z]{2}/.test(patent.patent_number) ? patent.patent_number.replace(/-/g, '') : 'US' + patent.patent_number.replace(/-/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: '500', color: '#2563eb', textDecoration: 'none' }}
                        >
                          {patent.patent_number}
                        </a>
                        {patent.note && <div style={{ fontSize: '14px', color: '#5e778a', marginTop: '4px' }}>{patent.note}</div>}
                        <div style={{ fontSize: '12px', color: '#5e778a', marginTop: '8px' }}>
                          Shared by {patent.shared_by} on {new Date(patent.shared_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={() => loadComments(patent.patent_number)}
                          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px' }}
                        >
                          {expandedPatent === patent.patent_number ? 'Hide Comments' : 'Comments'}
                        </button>
                        {(selectedWorkspace.my_role === 'owner' || selectedWorkspace.my_role === 'admin' || patent.shared_by === user.email) && (
                          <button
                            onClick={() => removePatentFromWorkspace(patent.patent_number)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {expandedPatent === patent.patent_number && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                        {loadingComments ? (
                          <p style={{ color: '#5e778a', fontSize: '13px' }}>Loading comments...</p>
                        ) : (
                          <>
                            {patentComments.length === 0 ? (
                              <p style={{ color: '#5e778a', fontSize: '13px', marginBottom: '12px' }}>No comments yet</p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                {patentComments.map((comment) => (
                                  <div key={comment.id} style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <div style={{ fontSize: '13px', color: '#1d2934' }}>{comment.comment}</div>
                                      {comment.user_email === user.email && (
                                        <button
                                          onClick={() => deleteComment(comment.id, patent.patent_number)}
                                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px', marginLeft: '8px' }}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#5e778a', marginTop: '4px' }}>
                                      {comment.user_email} - {new Date(comment.created_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Add a comment..."
                                style={{ flex: 1, padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '13px' }}
                                onKeyPress={(e) => e.key === 'Enter' && addComment(patent.patent_number)}
                              />
                              <button
                                onClick={() => addComment(patent.patent_number)}
                                style={{ padding: '8px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                              >
                                Add
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Members */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1d2934', marginBottom: '16px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
              Members ({selectedWorkspace.members?.length || 0})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedWorkspace.members?.map((member) => (
                <div key={member.user_email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#f5f7fa', borderRadius: '4px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#1d2934' }}>{member.user_email}</div>
                    <div style={{ fontSize: '12px', color: '#5e778a', textTransform: 'capitalize' }}>{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Transfer Ownership Modal */}
        <TransferOwnershipModal
          show={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          workspace={selectedWorkspace}
          onTransfer={transferOwnership}
        />
      </main>
    );
  }

  // Workspaces list view
  return (
    <main style={{ maxWidth: '900px', margin: '32px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1d2934' }}>Workspaces</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowJoinModal(true)}
            style={{ background: 'white', color: '#1d2934', padding: '10px 20px', borderRadius: '4px', border: '1px solid #e0e0e0', cursor: 'pointer', fontSize: '15px' }}
          >
            Join Workspace
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ background: '#53dafc', color: 'white', padding: '10px 20px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '15px' }}
          >
            Create Workspace
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#5e778a' }}>Loading workspaces...</p>
      ) : workspaces.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '8px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#5e778a', marginBottom: '16px' }}>You don't have any workspaces yet.</p>
          <p style={{ color: '#1d2934' }}>Create a workspace to start collaborating with your team, or join an existing one with an invite link.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {workspaces.map((workspace) => (
            <div
              key={workspace.id}
              onClick={() => loadWorkspaceDetails(workspace.id)}
              style={{ background: 'white', borderRadius: '8px', padding: '20px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid transparent', transition: 'border-color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#53dafc'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1d2934', marginBottom: '4px' }}>{workspace.name}</h3>
                  {workspace.description && <p style={{ color: '#5e778a', fontSize: '14px' }}>{workspace.description}</p>}
                </div>
                <span style={{ fontSize: '12px', color: '#5e778a', textTransform: 'capitalize', background: '#f5f7fa', padding: '4px 8px', borderRadius: '4px' }}>
                  {workspace.role}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '13px', color: '#5e778a' }}>
                <span>{workspace.member_count} member{workspace.member_count !== 1 ? 's' : ''}</span>
                <span>{workspace.patent_count} patent{workspace.patent_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transfer Ownership Modal */}
      <TransferOwnershipModal
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        workspace={selectedWorkspace}
        onTransfer={transferOwnership}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1d2934', marginBottom: '16px' }}>Create Workspace</h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#5e778a', marginBottom: '4px' }}>Workspace Name *</label>
              <input
                type="text"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="e.g., TTO Engineering Team"
                style={{ width: '100%', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '15px' }}
              />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#5e778a', marginBottom: '4px' }}>Description (optional)</label>
              <textarea
                value={newWorkspaceDesc}
                onChange={(e) => setNewWorkspaceDesc(e.target.value)}
                placeholder="What is this workspace for?"
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '15px', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateModal(false)} style={{ padding: '10px 20px', border: '1px solid #e0e0e0', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={createWorkspace} style={{ padding: '10px 20px', border: 'none', borderRadius: '4px', background: '#53dafc', color: 'white', cursor: 'pointer' }}>
                Create Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Modal */}
      {showJoinModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1d2934', marginBottom: '16px' }}>Join Workspace</h2>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#5e778a', marginBottom: '4px' }}>Invite Code</label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Paste invite code here"
                style={{ width: '100%', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '15px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowJoinModal(false)} style={{ padding: '10px 20px', border: '1px solid #e0e0e0', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={joinWorkspace} style={{ padding: '10px 20px', border: 'none', borderRadius: '4px', background: '#53dafc', color: 'white', cursor: 'pointer' }}>
                Join Workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}


export default WorkspacesContent;
